import type { ReactNode } from 'react';
import { Icon, type IconName } from '../icons/icons.js';
import styles from './patterns.module.css';

/**
 * TESSMA ONE module-page patterns — v1.1 ADDITIVE.
 *
 * TWO COMPONENTS, BOTH EARNED BY A SCREEN THAT EXISTS. This file is
 * deliberately not a framework: each one replaces a specific arrangement that
 * Finance was building by hand and that every later module would otherwise
 * copy.
 *
 *   NavTile   — Finance's landing page rendered fifteen destinations as
 *               `variant="secondary"` buttons inside four cards, so a place to
 *               go and a thing to do were the same shape and nothing read as
 *               the primary action.
 *
 *   StatTile  — Finance's overview rendered one Card per metric, each holding
 *               a single number, and a REFUSED metric as an empty state the
 *               same size as a figure.
 *
 * NO NEW TOKENS. `--fs-kpi-value`, `--fs-kpi-label` and `--fs-kpi-support`
 * have been in the frozen set since v1.0 with nothing using them; this is the
 * arrangement they were frozen for. Colours, radii, spacing and shadow are all
 * existing values.
 *
 * Nothing here decides what a caller may see. Both take what they render.
 */

export interface NavTileProps {
  readonly label: string;
  /** One line of context — a count, a total, a state. Omitted when unknown. */
  readonly supporting?: string | undefined;
  readonly icon?: IconName | undefined;
  readonly onClick: () => void;
}

/**
 * A place to go.
 *
 * A BUTTON, NOT A CARD WITH A CLICK HANDLER: it is one tab stop, it has one
 * accessible name, and it takes Enter and Space without being told to. The
 * chevron is decorative — the label is the name.
 *
 * `supporting` is optional on purpose. A tile that must show a number forces
 * every caller to find one, and a fabricated count is worse than no count.
 */
export function NavTile({ label, supporting, icon, onClick }: NavTileProps) {
  return (
    <button type="button" className={styles.navTile} onClick={onClick}>
      {icon !== undefined && (
        <span className={styles.navTileIcon} aria-hidden="true">
          <Icon name={icon} size={17} />
        </span>
      )}
      <span className={styles.navTileText}>
        <span className={styles.navTileLabel}>{label}</span>
        {supporting !== undefined && <span className={styles.navTileSupporting}>{supporting}</span>}
      </span>
      <span className={styles.navTileChevron} aria-hidden="true">
        <Icon name="caret-right" size={12} />
      </span>
    </button>
  );
}

/** Tiles in a responsive grid. Collapses to one column on a narrow viewport. */
export function NavTileGrid({ children }: { readonly children: ReactNode }) {
  return <div className={styles.navTileGrid}>{children}</div>;
}

export interface StatTileProps {
  readonly label: string;
  /**
   * The figure, already formatted by the caller.
   *
   * MONEY IS FORMATTED FROM THE SERVER'S MINOR-UNIT STRING, never computed
   * here. A tile that did arithmetic would be a second source of a number the
   * ledger already holds.
   */
  readonly value?: string | undefined;
  readonly caption?: string | undefined;
  /**
   * The server's own reason this figure is not shown, when it refused it.
   *
   * A REFUSED FIGURE IS NOT ZERO. Zero is a claim — "there are none of these" —
   * and a caller who may not see a total has been told nothing about whether it
   * is large or small. The tile says so in words and shows no number.
   *
   * The heading above the reason is fixed at "Not available for your access":
   * it is the wording the Dashboard and Finance already use for exactly this,
   * and a figure that is refused in one place and "restricted" in another
   * reads as two different things happening.
   */
  readonly unavailable?: string | undefined;
}

export function StatTile({ label, value, caption, unavailable }: StatTileProps) {
  const refused = unavailable !== undefined;
  return (
    <div className={styles.statTile}>
      <span className={styles.statLabel}>{label}</span>
      {refused ? (
        <span className={styles.statRefused}>
          <span className={styles.statRefusedIcon} aria-hidden="true">
            <Icon name="lock-simple" size={13} />
          </span>
          <span className={styles.statRefusedText}>
            <span className={styles.statRefusedTitle}>Not available for your access</span>
            <span>{unavailable}</span>
          </span>
        </span>
      ) : (
        <>
          {/*
           * The figure carries its own name because the label above it is a
           * separate element: without this, the only way to ask for one tile's
           * total among several is to know which one it was on screen.
           */}
          <span className={styles.statValue} aria-label={`${label} value`}>
            {value ?? '—'}
          </span>
          {caption !== undefined && <span className={styles.statCaption}>{caption}</span>}
        </>
      )}
    </div>
  );
}

/** A row of figures. One column on a phone, two on a tablet, four on desktop. */
export function StatTileRow({ children }: { readonly children: ReactNode }) {
  return <div className={styles.statTileRow}>{children}</div>;
}

export interface PageHeaderProps {
  /**
   * One line saying what this page is. Not a repeat of its name.
   *
   * THE PAGE TITLE IS NOT A PROP, ON PURPOSE. The shell already renders the
   * breadcrumb and the `<h1>`, both read from the same route table, and three
   * surfaces — Sales invoices, Supplier bills and Reports — were printing the
   * same words again in an `<h3>` directly underneath it. That is a duplicate
   * name, a heading level skipped from 1 to 3, and one more thing to keep in
   * step when a page is renamed. Passing a title here would let it come back.
   */
  readonly description?: string | undefined;
  /**
   * What this page is for doing — the primary action first.
   *
   * It sits beside the page rather than inside a card, because an action that
   * belongs to the whole page and an action that belongs to one panel on it
   * were previously the same thing in the same place.
   *
   * WHETHER AN ACTION IS OFFERED IS STILL THE CALLER'S DECISION, and still a
   * courtesy: the route guard refuses the URL typed by hand and the server
   * refuses the request again. This component only renders what it is given.
   */
  readonly actions?: ReactNode;
}

/** The description and actions belonging to the page the shell has named. */
export function PageHeader({ description, actions }: PageHeaderProps) {
  // Nothing to say and nothing to do: render nothing, rather than an empty
  // band of whitespace above the first card.
  if (description === undefined && actions === undefined) return null;
  return (
    <div className={styles.pageHeader}>
      {description !== undefined && <p className={styles.pageDescription}>{description}</p>}
      {actions !== undefined && <div className={styles.pageActions}>{actions}</div>}
    </div>
  );
}
